import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { useChat } from '../../hooks/useChat';
import DesktopChatWindow from './DesktopChatWindow';
import MobileChatWindow from './MobileChatWindow';
import DraggableBubble from './DraggableBubble';
import { AnimatePresence } from 'framer-motion';
import { DOCK_MARGIN } from './windowLayout';

const ChatSystem = () => {
    const {
        activeChats,
        minimizedChats,
        closeChat,
        minimizeChat,
        maximizeChat,
        startCall,
        unread
    } = useChat();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [zIndices, setZIndices] = useState({});
    const [maxZ, setMaxZ] = useState(100);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const bringToFront = (chatId) => {
        setZIndices(prev => ({
            ...prev,
            [chatId]: maxZ + 1
        }));
        setMaxZ(prev => prev + 1);
    };

    return (
        <>
            {/* DESKTOP DOCK — a row anchored to the bottom-right.
                Windows used to be absolutely positioned and draggable, which is how they
                ended up stacked on each other and hanging off the screen. Flexbox cannot
                place them wrongly: they sit side by side, right to left, and the row is
                pinned inside the viewport. `pointer-events-none` on the row keeps the page
                behind it clickable in the gaps. */}
            {!isMobile && activeChats.length > 0 && (
                <div
                    className="fixed z-40 pointer-events-none flex items-end gap-3"
                    style={{
                        right: DOCK_MARGIN + 72,   // clear of the minimised bubbles' column
                        bottom: DOCK_MARGIN,
                        maxWidth: `calc(100vw - ${DOCK_MARGIN * 2 + 72}px)`,
                    }}
                >
                    {/* Reversed so the MOST RECENT conversation sits nearest the corner,
                        where the eye and the cursor already are. */}
                    {[...activeChats].reverse().map((chat) => (
                        <div key={chat.id} className="pointer-events-auto flex-shrink-0">
                            <DesktopChatWindow
                                chat={chat}
                                onClose={() => closeChat(chat.id)}
                                onMinimize={() => minimizeChat(chat.id)}
                                onFocus={bringToFront}
                                zIndex={zIndices[chat.id] || 100}
                                onStartCall={startCall}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Mobile: ONE full-screen conversation at a time.
                Every active chat used to render its own full-screen overlay, so opening a
                second conversation stacked it on top of the first — only the last was
                usable, and closing it revealed another the user had forgotten about.
                `activeChats[0]` is the most recently opened (OPEN_CHAT prepends), so the
                front chat is shown and the rest stay in the list until chosen. */}
            {isMobile && (
                <AnimatePresence>
                    {activeChats.slice(0, 1).map((chat) => (
                        <MobileChatWindow
                            key={chat.id}
                            chat={chat}
                            onClose={() => closeChat(chat.id)}
                            onMinimize={() => minimizeChat(chat.id)}
                            onStartCall={startCall}
                        />
                    ))}
                </AnimatePresence>
            )}

            {/* MINIMISED — a fixed column at the right, newest nearest the bottom.
                Bubbles used to be individually draggable with saved positions, which
                scattered them down the page and over the app's own content. */}
            {!isMobile && minimizedChats.length > 0 && (
                <div
                    className="fixed z-50 flex flex-col-reverse gap-3"
                    style={{ right: DOCK_MARGIN, bottom: DOCK_MARGIN }}
                >
                    {minimizedChats.slice(0, 6).map((chat) => (
                        <DraggableBubble
                            key={chat.id}
                            chat={chat}
                            onMaximize={maximizeChat}
                            onClose={closeChat}
                            unreadCount={(unread && unread[chat.id]) || 0}
                        />
                    ))}
                </div>
            )}

        </>
    );
};

export default ChatSystem;
